/**
 * Which route a prerendered shell actually contains — stamped by the writer,
 * read by the client mount harness.
 *
 * ── THE FAILURE ─────────────────────────────────────────────────────────────
 *
 * `mountClient` hydrated whenever `#root` had children. For a route that was
 * never prerendered — a param/detail route like `/faculty/:slug`, or `/apply` —
 * CloudFront's SPA fallback serves the HOME shell, which has children. So React
 * hydrated the home page's markup against a different route's tree: a guaranteed
 * mismatch, after which React discards the server markup and rebuilds client
 * side. The page then looks correct, so nothing surfaces it, while the SSR work
 * and the crawlable first paint it exists for are thrown away.
 *
 * Measured live on 2026-09-07 before this existed:
 *   music.garrettjohnlaw.com/blog/does-not-exist-xyz
 *     → HTTP 200, 36,867 bytes, `<title>Home | Garrett John Law Music`
 *   music.garrettjohnlaw.com/
 *     → HTTP 200, 36,867 bytes, byte-identical
 *
 * Four sites using `mountClient` have param routes; two of them are live.
 *
 * ── WHY A STAMP AND NOT A PER-SITE LIST ─────────────────────────────────────
 *
 * `chroma-festival-v2` had the right guard: a hand-maintained `PRERENDERED` set
 * in its `main.tsx`, whose own comment concedes it must be kept in sync with the
 * route table and `seo.json`. That is the shape `fallback-route.tsx` warns
 * about — "a rule that must be re-implemented per site is a rule most sites will
 * not have" — and it was in exactly one of the five sites that need it.
 *
 * The writer already knows which path it rendered. Stamping it removes the list.
 *
 * ── THREE STATES, AND ABSENT IS NOT MISMATCHED ──────────────────────────────
 *
 * Same discipline `stamp-site-kit.mjs` records for its own meta tag: a missing
 * stamp and a stamp that disagrees are different facts.
 *
 *   absent     — HTML written before stamping existed (the whole fleet, until
 *                each site is next published). Behave EXACTLY as before:
 *                hydrate if there are children. Backward compatibility is the
 *                point — a published page must not stop hydrating because the
 *                reader shipped ahead of the writer.
 *   matches    — this shell is this route. Hydrate.
 *   differs    — this shell is some OTHER route (the SPA fallback). Render
 *                fresh, which is what React would have ended up doing anyway,
 *                minus the mismatch.
 *
 * So the change can only ever convert a wrong hydration into a correct fresh
 * render. It cannot stop a correct hydration.
 */
/** The attribute the writers stamp onto `<div id="root">`. */
export const PRERENDERED_PATH_ATTR = 'data-prerendered-path';
/**
 * Canonical path form, so writer and reader cannot disagree over a trailing
 * slash. Query and hash are dropped: they never change which route rendered,
 * and `?utm_source=…` must not defeat hydration on every shared link.
 */
export function normalizePrerenderedPath(path) {
    const withoutQuery = path.split('?')[0].split('#')[0];
    const trimmed = withoutQuery.replace(/\/+$/, '');
    return trimmed === '' ? '/' : trimmed;
}
/** Minimal attribute-value escaping. Route paths are slugs, but the writer must
 *  not be the thing that trusts that. */
function escapeAttribute(value) {
    return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/**
 * The replacement for an empty `<div id="root"></div>`: the rendered body, plus
 * the stamp saying which route it is. Used by BOTH writers —
 * `scripts/prerender-bespoke.mjs` (build-time SSG) and the publish Lambda
 * (`@gjl/cms` publish/factory.ts) — because a page that only one of them stamps
 * is a page whose hydration depends on which tool wrote it.
 */
export function prerenderedRootHtml(body, path) {
    const stamp = escapeAttribute(normalizePrerenderedPath(path));
    return `<div id="root" ${PRERENDERED_PATH_ATTR}="${stamp}">${body}</div>`;
}
/** What the mount harness should do. Pure, so the three states are testable
 *  without a DOM. */
export function hydrationDecision(stamp, currentPath, hasChildren) {
    if (!hasChildren)
        return 'render';
    // Absent stamp — pre-stamping HTML. Legacy behaviour, deliberately.
    if (stamp == null || stamp === '')
        return 'hydrate';
    return normalizePrerenderedPath(stamp) === normalizePrerenderedPath(currentPath) ? 'hydrate' : 'render';
}
